import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { localDayKey } from '../../utils/localDate';
import { getAppLocale } from '../../utils/locale';
import { useMoney } from '../../hooks/useMoney';
import '../../styles/components/FormPage.css';
import { parseIdFromPath } from '../../utils/jiraId';
import Icon from '../Icon';
import { Input as FormInput } from '../FormField';
import SearchableSelect from '../ui/SearchableSelect';
import RichTextEditor from '../ui/RichTextEditor';
import { useLocation } from 'react-router-dom';
import { loadBudget, saveBudget, netPaid, apiUpdatePayment, createPaymentOrThrow, updatePaymentOrThrow, refreshBudgetFromApi } from '../../utils/budgetStorage';
import { errorMessageOf } from '../../utils/apiErrors';
import { useFormSaveEvent, useClearErrorsOnEdit } from './_shared';
import type { Payment, PaymentMethod, PaymentStatus } from '../../types/ui/Budget';
import { useNotifications } from '../NotificationProvider';
import { uploadImage, deleteAttachment, getAttachments, resolveMediaUrl, type Attachment } from '../../services/uploadService';
import { loadSettingsFromDb } from '../../services/SettingsService';
import { loadContributors } from '../../services/ContributorService';
import { Contributor } from '../../types/ui/Settlement';
import { useCoupleNames } from '../../hooks/useCoupleNames';
import { resolvePayerSelect, CENTRAL_POT_VALUE, OTHER_VALUE } from '../../utils/payerOptions';
import RecordActivity from '../ui/RecordActivity';
import { MaskedValue } from '../ui/MaskedValue';

const PAYMENT_COMMITTED_STATUSES = ['booked', 'contract-signed', 'paid-in-full'];
const PAYMENT_METHODS_LIST: { value: PaymentMethod; label: string }[] = [
  { value: 'bank-transfer', label: 'Bank Transfer' },
  { value: 'cash', label: 'Cash' },
  { value: 'card', label: 'Card' },
  { value: 'mobile-money', label: 'Mobile Money' },
  { value: 'check', label: 'Cheque' },
];

type PaymentEntryType = NonNullable<Payment['entryType']>;

interface SavedPaymentMethod {
  bankName?: string;
  accountName?: string;
  accountNumber?: string;
  reference?: string;
}

interface RecordPaymentFormPageRow {
  id: string;
  vendor: string;
  category: string;
  totalCost: number;
  totalPaid: number;
  balance: number;
}

interface RecordPaymentFormPageProps {
  preselectedId?: string;
  onSave: () => void;
  onCancel: () => void;
}

export const RecordPaymentFormPage: React.FC<RecordPaymentFormPageProps> = ({ preselectedId, onSave, onCancel }) => {
  const money = useMoney();
  const { showSuccess, showError } = useNotifications();
  const { names: honorees } = useCoupleNames();
  const location = useLocation();
  const editState = location.state as { editPayment?: Payment; vendorId?: string } | null;
  const editPayment = editState?.editPayment ?? null;
  const editVendorId = editState?.vendorId ?? null;
  // Detect edit mode from URL even after hard refresh (no route state)
  const urlPaymentId = useMemo(
    () => parseIdFromPath('PY', location.pathname, '/payments/record/'),
    [location.pathname],
  );
  const isEditMode = editPayment !== null || urlPaymentId !== null;
  const [currentStatus, setCurrentStatus] = useState<Payment['status'] | null>(editPayment?.status ?? null);
  // A completed payment already moved money — editing it after the fact would
  // silently rewrite history. Void + re-record is the only way to correct it.
  const isLocked = isEditMode && currentStatus === 'completed';

  const today = localDayKey();
  const paymentIdRef = useRef(editPayment?.id ?? `pay_${Date.now()}`);

  // Receipts were uploaded against entity_type='payment' but never fetched
  // back, so reopening a payment showed none of its proof-of-payment files.
  useEffect(() => {
    if (!editPayment?.id) return;
    getAttachments('payment', editPayment.id)
      .then(setAttachments)
      .catch(() => { /* leave the list empty rather than blocking the form */ });
  }, [editPayment?.id]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [rows, setRows] = useState<RecordPaymentFormPageRow[]>([]);

  // This project's own saved bank details (never a browser-wide copy that another project wrote).
  const [savedPaymentMethod, setSavedPaymentMethod] = useState<SavedPaymentMethod>({});
  useEffect(() => {
    let alive = true;
    loadSettingsFromDb()
      .then(db => { if (alive) setSavedPaymentMethod(db.project.paymentMethod ?? {}); })
      .catch(() => { /* no bank details block rather than someone else's */ });
    return () => { alive = false; };
  }, []);

  const [vendorId, setVendorId] = useState(editVendorId ?? preselectedId ?? '');
  const [amount, setAmount] = useState(editPayment ? String(editPayment.amount) : '');
  const [method, setMethod] = useState<PaymentMethod>(editPayment?.method ?? 'bank-transfer');
  // payments.type only ever holds 'payment' or 'refund'. The deposit/
  // instalment/retainer list this used to offer was never a column, so nothing
  // ever came back from the API and every payment re-opened reading "Deposit".
  const [entryType, setEntryType] = useState<PaymentEntryType>(editPayment?.entryType ?? 'payment');
  const [date, setDate] = useState(editPayment?.date || today);
  const [reference, setReference] = useState(editPayment?.reference ?? '');
  const [paidBy, setPaidBy] = useState((editPayment as any)?.paid_by ?? '');
  // The payer the saved payment had, so an old name stays selectable instead of turning into free text.
  const [legacyPaidBy, setLegacyPaidBy] = useState<string>((editPayment as any)?.paid_by ?? '');
  const [isGift, setIsGift] = useState<boolean>((editPayment as any)?.is_gift ?? false);
  const [notes, setNotes] = useState(editPayment?.notes ?? '');
  const [saving, setSaving] = useState(false);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [uploading, setUploading] = useState(false);
  const [contributors, setContributors] = useState<Contributor[]>([]);

  const selected = rows.find(r => r.id === vendorId);
  const parsedAmount = parseFloat(amount);
  const remainingAfter = selected && parsedAmount > 0
    ? Math.max(0, selected.balance - parsedAmount)
    : null;

  useEffect(() => {
    window.addEventListener('payment-record-cancel', onCancel);
    return () => window.removeEventListener('payment-record-cancel', onCancel);
  }, [onCancel]);

  useEffect(() => {
    refreshBudgetFromApi().then(cats => {
      const result: RecordPaymentFormPageRow[] = [];
      for (const cat of cats) {
        for (const v of cat.vendors) {
          const committed = PAYMENT_COMMITTED_STATUSES.includes(v.booking_status ?? '') || v.payments.some(p => p.status === 'completed');
          if (!committed) continue;
          // The line's cost is its planned cost (actual_cost is what has been paid), and only
          // payments in the budget's currency count towards it.
          const totalCost = v.plannedCostBase;
          const totalPaid = netPaid(v.payments);
          const balance = totalCost - totalPaid;
          // A fully-paid vendor has nothing left to record a payment
          // against — exclude it from the picker, except when it's the
          // vendor already attached to the payment being edited (that one
          // must stay selectable/visible even at a zero balance).
          const isCurrentSelection = v.vendor_id === editVendorId || v.vendor_id === preselectedId;
          if (balance <= 0 && !isCurrentSelection) continue;
          result.push({
            id: v.vendor_id,
            vendor: v.vendor_name,
            category: cat.name,
            totalCost,
            totalPaid,
            balance,
          });
        }
      }
      setRows(result);
      // Pre-select first vendor if no id was provided — but only for a
      // genuinely new payment (!isEditMode, not just !editPayment: a hard
      // refresh or a direct/shared link to an existing payment's URL loses
      // location.state, so editPayment alone can't tell "new" from "edit
      // reloaded without state"). An edit of an existing payment with no
      // budget vendor (order-linked or asoebi-linked, e.g. opened from
      // OrderPaymentsPanel, which doesn't pass vendorId at all) has no
      // vendor to pre-select; forcing one in here silently attached the
      // edit to an unrelated vendor's budget line instead of leaving the
      // form showing the payment on its own.
      if (!isEditMode && !editVendorId && !preselectedId && result.length > 0) {
        setVendorId(result[0].id);
      }
    }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editVendorId, preselectedId]);

  useEffect(() => {
    loadContributors().then(setContributors).catch(() => {});
  }, []);

  // Hard-refresh recovery: if no route state but URL has a numeric payment ID, load from API
  useEffect(() => {
    if (editPayment !== null || !urlPaymentId) return;
    refreshBudgetFromApi().then(categories => {
      for (const cat of categories) {
        for (const vendor of cat.vendors) {
          const payment = vendor.payments.find(p => p.id === urlPaymentId);
          if (payment) {
            setVendorId(vendor.vendor_id);
            setAmount(String(payment.amount));
            setMethod(payment.method ?? 'bank-transfer');
            setDate(payment.date || today);
            setReference(payment.reference ?? '');
            setPaidBy((payment as any).paid_by ?? '');
            setLegacyPaidBy((payment as any).paid_by ?? '');
            setNotes(payment.notes ?? '');
            setEntryType(payment.entryType ?? 'payment');
            setCurrentStatus(payment.status);
            paymentIdRef.current = payment.id;
            return;
          }
        }
      }
    }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlPaymentId]);

  const handleFiles = useCallback(async (files: FileList | null) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      const uploaded = await Promise.all(
        Array.from(files).map((file, i) =>
          uploadImage(file, {
            entityType: 'payment',
            entityId: paymentIdRef.current,
            label: file.name,
            sortOrder: attachments.length + i,
          }),
        ),
      );
      setAttachments(prev => [...prev, ...uploaded]);
    } catch {
      showError('Failed to upload file');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }, [attachments.length, showError]);

  const handleRemoveAttachment = async (att: Attachment) => {
    try {
      await deleteAttachment(att.id);
      setAttachments(prev => prev.filter(a => a.id !== att.id));
    } catch {
      showError('Failed to remove attachment');
    }
  };

  const handleVoid = async () => {
    if (!editPayment) return;
    setSaving(true);
    try {
      await updatePaymentOrThrow(editPayment.id, { status: 'failed' });
      const cats = loadBudget();
      const updated = cats.map(cat => ({
        ...cat,
        vendors: cat.vendors.map(v => {
          if (v.vendor_id !== vendorId) return v;
          const np = v.payments.map(p => p.id === editPayment.id ? { ...p, status: 'failed' as const } : p);
          const totalPaid = netPaid(np);
          const cost = v.plannedCostBase;
          const newStatus: PaymentStatus = totalPaid === 0 ? 'not-started' : totalPaid >= cost ? 'fully-paid' : 'partially-paid';
          return { ...v, payments: np, payment_status: newStatus };
        }),
      }));
      saveBudget(updated);
      showSuccess('Payment voided — it no longer counts toward totals.');
      onSave();
    } catch (err) {
      showError(errorMessageOf(err, 'Failed to void payment'));
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    window.addEventListener('payment-record-void', handleVoid);
    return () => window.removeEventListener('payment-record-void', handleVoid);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editPayment, vendorId]);

  const handleSave = async () => {
    if (isLocked) { showError('This payment is locked — void it to correct a mistake.'); return; }
    const amt = parseFloat(amount);
    // A vendor is only required to CREATE a payment (apiCreatePayment takes
    // vendorId as its target). Editing an existing payment PATCHes by its
    // own id and never needed one — requiring it here blocked saving any
    // edit to a payment with no budget vendor (order-linked, asoebi-linked),
    // which never gets one pre-selected (see the editPayment guard above).
    if (!isEditMode && !vendorId) { showError('Select a vendor'); return; }
    if (!amt || amt <= 0) { showError('Enter a valid amount'); return; }
    if (!date) { showError('Enter a date'); return; }
    setSaving(true);
    try {
      if (isEditMode && editPayment) {
        // entryType is deliberately absent: the API's payment PATCH allow-list
        // excludes `type`, because flipping a payment into a refund after the
        // fact rewrites what the money already did. Void and re-record instead.
        const updates: Partial<Payment> = { amount: amt, date, method, status: 'completed', reference: reference.trim() || undefined, paid_by: paidBy.trim() || undefined, notes: notes.trim() || undefined, is_gift: paidBy.trim() ? isGift : undefined } as any;
        await updatePaymentOrThrow(editPayment.id, updates);
        showSuccess('Payment updated');
      } else {
        const newPayment: Payment = {
          id: paymentIdRef.current,
          amount: amt,
          date,
          method,
          entryType,
          status: 'completed',
          reference: reference.trim() || undefined,
          paid_by: paidBy.trim() || undefined,
          notes: notes.trim() || undefined,
          is_gift: paidBy.trim() ? isGift : undefined,
        } as any;
        await createPaymentOrThrow(vendorId, newPayment);
        showSuccess('Payment recorded');
      }
      onSave();
    } finally {
      setSaving(false);
    }
  };

  // handleSave throws when the API refuses the payment; the shared hook then
  // shows a 422's field errors under their fields (the API's names for them)
  // or a plain-language toast, and the form stays open.
  const fieldErrors = useFormSaveEvent(
    () => (saving ? undefined : handleSave()),
    { event: 'payment-record-save', fallback: 'Failed to save payment.' },
  );
  useClearErrorsOnEdit(
    { budget_item_id: vendorId, amount, date, type: entryType, method, notes, paid_by: paidBy, reference },
    fieldErrors,
  );

  const showBankDetails = method === 'bank-transfer' && (
    savedPaymentMethod.bankName ||
    savedPaymentMethod.accountName ||
    savedPaymentMethod.accountNumber
  );

  return (
    <>
    <div className="detail-layout">
      <div className="inline-form-page">
        {isLocked && (
          <p className="entity-state-banner entity-state-banner--locked form-section-title--flush">
            This payment is completed and locked. Use Void (in the header) to reverse it instead of editing.
          </p>
        )}
        <div className="form-group">
          <label>Vendor / Contact <span className="form-required">*</span></label>
          <div style={isLocked ? { opacity: 0.6, pointerEvents: 'none' } : undefined}>
            <SearchableSelect
              value={vendorId}
              onChange={setVendorId}
              error={fieldErrors.errorFor('budget_item_id')}
              options={rows.map(r => ({ value: r.id, label: `${r.vendor} — ${r.category} (${money.format(r.balance)} due)` }))}
              placeholder="Search vendors or type a name…"
            />
          </div>
        </div>
        <div className="form-col-2">
          <FormInput label={`Amount (${money.unit})`} type="text" inputMode="numeric" placeholder="e.g. 500,000" value={amount ? Number(amount.replace(/,/g, '')).toLocaleString(getAppLocale()) : ''} onChange={e => setAmount(e.target.value.replace(/,/g, ''))} error={fieldErrors.errorFor('amount')} disabled={isLocked} required />
          <FormInput label="Date" type="date" value={date} onChange={e => setDate(e.target.value)} error={fieldErrors.errorFor('date')} disabled={isLocked} required />
        </div>
        <div className="form-col-2">
          <div className="form-group">
            <label className="form-label">Payment Type</label>
            <SearchableSelect value={entryType} onChange={value => setEntryType(value as PaymentEntryType)} error={fieldErrors.errorFor('type')} disabled={isLocked || isEditMode} options={[
              { value: 'payment', label: 'Payment' },
              { value: 'refund',  label: 'Refund' },
            ]} />
            {isEditMode && !isLocked && (
              <p className="form-hint">Fixed when the entry was recorded — void and re-record to change it.</p>
            )}
          </div>
          <div className="form-group">
            <label className="form-label">Payment Method</label>
            <SearchableSelect value={method} onChange={value => setMethod(value as PaymentMethod)} error={fieldErrors.errorFor('method')} disabled={isLocked} options={PAYMENT_METHODS_LIST} />
          </div>
        </div>
        <RichTextEditor label="Notes" placeholder="Any additional context…" rows={3} value={notes} onChange={val => setNotes(val)} error={fieldErrors.errorFor('notes')} disabled={isLocked} />
      </div>

      <aside className="detail-sidebar">
        <section className="side-section">
          <h4>Balance</h4>
          {selected ? (
            <>
              <div className="stat-row"><span>Vendor</span><strong>{selected.vendor}</strong></div>
              <div className="stat-row"><span>Category</span><strong>{selected.category}</strong></div>
              <div className="stat-row"><span>Total cost</span><strong>{money.format(selected.totalCost)}</strong></div>
              <div className="stat-row"><span>Paid so far</span><strong>{money.format(selected.totalPaid)}</strong></div>
              <div className="stat-row">
                <span>Outstanding</span>
                <strong className={selected.balance > 0 ? 'rpf-balance-due' : 'rpf-balance-clear'}>
                  {money.format(selected.balance)}
                </strong>
              </div>
              {remainingAfter !== null && (
                <div className="stat-row rpf-stat-highlight">
                  <span>After this payment</span>
                  <strong>{money.format(remainingAfter)}</strong>
                </div>
              )}
            </>
          ) : (
            <p className="rpf-sidebar-empty">Select a vendor to see balance details.</p>
          )}
        </section>

        <section className="side-section">
          <h4>Paid by</h4>
          {(() => {
            const CENTRAL_POT = CENTRAL_POT_VALUE;
            // The couple (honoree guests) aren't contributors (they're not gifting money
            // toward their own project, or owed reimbursement for it) and
            // they aren't "Central Pot" either — that implies the shared
            // joint fund specifically, which loses the information that it
            // was one of them personally, from their own account. Pinned as
            // their own options instead of falling through to free-text
            // "Other person", same as any named contributor.
            const coupleNames = honorees;
            const contributorNames = contributors.map(c => c.name);
            const { selectValue, isCentralPot, isOther, pinned } = resolvePayerSelect({
              paidBy, legacyPaidBy, couple: coupleNames, contributors: contributorNames,
            });
            const isNamedPerson = paidBy !== '' && !isCentralPot;
            return (
              <>
                <div style={{ /* ds-dynamic: spacing depends on payer-type selection */ width: '100%', marginBottom: (isOther || isCentralPot) ? 6 : 0 }}>
                  <SearchableSelect
                    className="form-select"
                    disabled={isLocked}
                    error={fieldErrors.errorFor('paid_by')}
                    value={selectValue}
                    onChange={value => {
                      if (value === CENTRAL_POT) setPaidBy('Central Pot');
                      else if (value === OTHER_VALUE) setPaidBy('');
                      else setPaidBy(value);
                    }}
                    placeholder="— Select payer —"
                    options={[
                      { value: '', label: '— Select payer —' },
                      ...coupleNames.map(name => ({ value: name, label: name })),
                      ...(pinned ? [{ value: pinned, label: pinned }] : []),
                      { value: CENTRAL_POT, label: 'Central Pot / Couple account' },
                      ...contributors.map(c => ({ value: c.name, label: c.name })),
                      { value: OTHER_VALUE, label: 'Other person…' },
                    ]}
                  />
                </div>
                {isCentralPot && (
                  <p className="rpf-central-pot-note">Paid from the shared fund — no individual reimbursement needed.</p>
                )}
                {isOther && (
                  <FormInput
                    label=""
                    placeholder="Name of person who paid"
                    value={paidBy}
                    onChange={e => setPaidBy(e.target.value)}
                    disabled={isLocked}
                  />
                )}
                {isNamedPerson && !isOther && (
                  <label className="rpf-gift-toggle">
                    <input type="checkbox" checked={isGift} onChange={e => setIsGift(e.target.checked)} disabled={isLocked} />
                    <span>This is a gift — no reimbursement needed</span>
                  </label>
                )}
                {isOther && paidBy.trim() && (
                  <label className="rpf-gift-toggle rpf-gift-toggle--spaced">
                    <input type="checkbox" checked={isGift} onChange={e => setIsGift(e.target.checked)} disabled={isLocked} />
                    <span>This is a gift — no reimbursement needed</span>
                  </label>
                )}
              </>
            );
          })()}
        </section>

        <section className="side-section">
          <FormInput
            label="Reference / Receipt No."
            placeholder="e.g. TRF-2026-001"
            value={reference}
            onChange={e => setReference(e.target.value)}
            error={fieldErrors.errorFor('reference')}
            disabled={isLocked}
          />
          {showBankDetails && (
            <div className="rpf-bank-details">
              <p className="rpf-bank-details-label">Your payment account</p>
              {savedPaymentMethod.bankName && (
                <div className="stat-row"><span>Bank</span><strong>{savedPaymentMethod.bankName}</strong></div>
              )}
              {savedPaymentMethod.accountName && (
                <div className="stat-row"><span>Account name</span><strong>{savedPaymentMethod.accountName}</strong></div>
              )}
              {savedPaymentMethod.accountNumber && (
                <div className="stat-row"><span>Account no.</span><strong>{savedPaymentMethod.accountNumber}</strong></div>
              )}
              {savedPaymentMethod.reference && (
                <p className="rpf-bank-hint"><MaskedValue value={savedPaymentMethod.reference} /></p>
              )}
            </div>
          )}
          <div className="rpf-divider" />
          <h4 className="rpf-attachments-heading">Receipts &amp; Attachments</h4>
          {attachments.length > 0 && (
            <ul className="rpf-attachment-list">
              {attachments.map(att => (
                <li key={att.id} className="rpf-attachment-item">
                  <a
                    href={resolveMediaUrl(att.url)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="rpf-attachment-link"
                    title={att.label ?? 'View attachment'}
                  >
                    <Icon name="document" size={14} />
                    <span>{att.label ?? 'Attachment'}</span>
                  </a>
                  <button
                    type="button"
                    className="rpf-attachment-remove"
                    onClick={() => { void handleRemoveAttachment(att); }}
                    aria-label="Remove attachment"
                  >
                    <Icon name="x" size={14} />
                  </button>
                </li>
              ))}
            </ul>
          )}
          <label
            className={`sidebar-photo-upload${attachments.length > 0 ? ' sidebar-photo-upload--mt' : ''}`}
            onClick={e => { e.preventDefault(); fileInputRef.current?.click(); }}
            onDragOver={e => e.preventDefault()}
            onDrop={e => { e.preventDefault(); void handleFiles(e.dataTransfer.files); }}
          >
            <span className="sidebar-photo-upload-btn">
              {uploading ? 'Uploading…' : '+ Attach receipt or proof'}
            </span>
          </label>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,.pdf,.doc,.docx"
            multiple
            className="u-hidden"
            onChange={e => { void handleFiles(e.target.files); }}
          />
          <p className="form-hint">PDF, Word, or image files. Drag and drop supported.</p>
        </section>
      </aside>
    </div>

    {/* A completed payment is locked against editing on purpose (void and
        re-record instead), which left nowhere at all to say WHY it was
        recorded the way it was, or that it was reconciled against a bank
        alert. Comments only: `payments.status` exists but nothing in the app
        transitions it — every row in production is 'completed' — so there is
        no trail to keep. Give it withStatusTracking() the day a real
        pending/failed lifecycle lands. */}
    <RecordActivity
      entityType="payment"
      entityId={editPayment?.id ?? urlPaymentId ?? undefined}
    />
    </>
  );
};
